import type { UIMatch } from 'react-router';
import { useMatches } from 'react-router';
import { useMemo } from 'react';
import type { APHandle } from '../type';
import type { HeaderHandle } from '../type/handle';

/**
 * This base hook is used to quickly search for specific handle data
 * across all loader data using useMatches.
 */

export const useMatchesHandleData = <T>(
    key: keyof APHandle,
    defaultValue: T,
    latestMatchFirst = true,
): T => {
    const matches = useMatches();
    const value = useMemo(() => {
        const newMatches = latestMatchFirst ? matches.reverse() : matches;
        const match = newMatches.find(
            (match) => (match.handle as APHandle)?.[key] !== undefined,
        ) as UIMatch<unknown, APHandle>;

        return match?.handle?.[key] ?? defaultValue;
    }, [matches, key, defaultValue, latestMatchFirst]);
    return value as T;
};

/**
 * Resolve the deepest matched route's `handle.contentClassName`, supporting
 * the loader-data function form as `useHeaderHandle` does.
 */
export const useContentClassName = (): string => {
    const matches = useMatches();
    return useMemo(() => {
        const match = [...matches]
            .reverse()
            .find(
                (m) =>
                    (m.handle as APHandle | undefined)?.contentClassName !==
                    undefined,
            ) as UIMatch<unknown, APHandle> | undefined;
        const value = match?.handle?.contentClassName;
        return (typeof value === 'function' ? value(match?.data) : value) ?? '';
    }, [matches]);
};

/**
 * Resolve the deepest matched route's `handle.header`, supporting the
 * loader-data function form so flag-gated routes can withhold (or vary)
 * their header treatment when the flag is off.
 */
export const useHeaderHandle = (): HeaderHandle | undefined => {
    const matches = useMatches();
    return useMemo(() => {
        const match = [...matches]
            .reverse()
            .find(
                (m) => (m.handle as APHandle | undefined)?.header !== undefined,
            ) as UIMatch<unknown, APHandle> | undefined;
        const header = match?.handle?.header;
        return typeof header === 'function' ? header(match?.data) : header;
    }, [matches]);
};
